import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  GegeVorbelegungdaten,
  mapVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { SagaIterator } from 'redux-saga';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { GegeVorvertragsdaten, SpartenType, VertragsdatenRequestModel } from '@ps20/bff-models';
import dayjs from 'dayjs';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { v1 as uuidv1 } from 'uuid';
import { AxiosError } from 'axios';
import { IAppState } from '../appState';
import { GegeActions } from '../gegeActions';
import { GegenstandsschutzItem } from '../reducers/gegenstandsschutz.reducer';

export enum VorbelegungGegeRequestEvents {
  VORBELEGUNG_GEGE_REQUEST_SUCCESSFUL = 'VORBELEGUNG_GEGE_REQUEST_SUCCESSFUL',
  VORBELEGUNG_GEGE_REQUEST_FAILED = 'VORBELEGUNG_GEGE_REQUEST_FAILED',
}

export function* watchVorbelegungRequested(): SagaIterator {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.GEGE));
  yield takeEvery(
    VorbelegungGegeRequestEvents.VORBELEGUNG_GEGE_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.GEGE),
  );
  yield takeEvery(
    VorbelegungGegeRequestEvents.VORBELEGUNG_GEGE_REQUEST_FAILED,
    safeSaga(errorCallback, App.GEGE),
  );
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>): SagaIterator {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.GEGE || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  if (!state.gegenstandsschutz.categories) {
    yield take(GlobalActions.GEGENSTAENDE_CATEGORIES_RECEIVED);
  }
  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* gegeDatenVorbelegen(
      action.payload.vorhandeneVorbelegungdaten,
      action.payload.angebotsId,
    );
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.GEGE,
        angebotsId: action.payload.angebotsId,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungGegeRequestEvents.VORBELEGUNG_GEGE_REQUEST_SUCCESSFUL,
        errorEvent: VorbelegungGegeRequestEvents.VORBELEGUNG_GEGE_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<GegeVorvertragsdaten>): SagaIterator {
  const state: IAppState = yield select();
  if (state.angebotsId !== successAction.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }
  const gegeVorbelegungdaten: GegeVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.GEGENSTANDSSCHUTZ,
    successAction.payload,
  ) as GegeVorbelegungdaten;
  yield* gegeDatenVorbelegen(
    gegeVorbelegungdaten,
    successAction.doRequestAction.payload.angebotsId,
  );
}

export function* gegeDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten, angebotsId: string): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const gegeVorbelegungdaten: GegeVorbelegungdaten = vorbelegungdaten
    ? (vorbelegungdaten as GegeVorbelegungdaten)
    : null;

  if (!state.produkt?.produktlinien || state.produkt?.produktlinien.length === 0) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }

  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.GEGE,
        state.angebotsId,
      ),
    );
  }

  gegeVorbelegungdaten.angebotsId = state.angebotsId;
  const versicherteGegenstaende: GegenstandsschutzItem[] =
    gegeVorbelegungdaten.versicherteGegenstaende.map(gegenstand => {
      return {
        id: uuidv1(),
        category: state.gegenstandsschutz.categories.find(
          category => category.name === gegenstand.category,
        ),
        purchaseDate: dayjs(gegenstand.kaufdatum, DATEFORMAT_SHORT_ISO).toDate(),
        insuranceValue: gegenstand.kaufpreis,
        model: gegenstand.bezeichnung,
        information: gegenstand.beschreibung,
      };
    });

  yield put(new GegeActions().gegenstaendeVorbelegt(versicherteGegenstaende, angebotsId));
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested({
      appId: App.GEGE,
      angebotsId,
      subroute: '/eingabe',
    }),
  );

  yield call(
    [globalPut, globalPut.put],
    globalActions.vorbelegungFinished({
      appId: App.GEGE,
      angebotsId,
      vorhandeneVorbelegungdaten: gegeVorbelegungdaten,
    }),
  );
}

export function* errorCallback(error: DoRequestResponse<AxiosError>): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  if (state.angebotsId !== error.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }
  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.GEGE,
      angebotsId: state.angebotsId,
    }),
  );
  yield cancel();
}
